import { useCallback, useState } from 'react';

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
}

interface UseConfirmResult {
  confirmState: ConfirmState;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  handleConfirm: () => void;
  handleCancel: () => void;
}

interface ConfirmOptions {
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

const DEFAULT_STATE: ConfirmState = {
  open: false,
  title: 'Are you sure?',
  message: '',
  confirmLabel: 'Yes',
  cancelLabel: 'Cancel',
  onConfirm: () => {},
};

export function useConfirm(): UseConfirmResult {
  const [confirmState, setConfirmState] = useState<ConfirmState>(DEFAULT_STATE);
  const [resolveRef, setResolveRef] = useState<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions = {}): Promise<boolean> => {
    return new Promise((resolve) => {
      setResolveRef(() => resolve);
      setConfirmState({
        open: true,
        title: options.title ?? 'Are you sure?',
        message: options.message ?? '',
        confirmLabel: options.confirmLabel ?? 'Yes',
        cancelLabel: options.cancelLabel ?? 'Cancel',
        onConfirm: () => {},
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setConfirmState(DEFAULT_STATE);
    resolveRef?.(true);
    setResolveRef(null);
  }, [resolveRef]);

  const handleCancel = useCallback(() => {
    setConfirmState(DEFAULT_STATE);
    resolveRef?.(false);
    setResolveRef(null);
  }, [resolveRef]);

  return { confirmState, confirm, handleConfirm, handleCancel };
}
